import { baseApi } from 'api'
import { invalidateTagsIfSuccess } from 'api/helpers'
import { ApiResponse } from 'helpers/types'
import { TOrderCreatePayload } from 'modules/order/types/orderCreateTypes'
import {
  OperationListResponse,
  OperationParamListResponse,
  OrderCopy,
  OrderCopyToCreateNewOrderResponse,
  OrderItemResponse,
  OrderListResponse,
  OrderYarcheCreateRequest,
  PaymentListResponse,
  PaymentTransactionCreateResponse,
  TerminalCheckoutScheduleResponse,
  TerminalListResponse,
  TrackingResponse
} from 'schema'

import {
  PaymentTransactionCreateRequestWithId,
  TCheckOutSchedule,
  TCheckOutScheduleRequestData,
  TOrder,
  TOrderPayment
} from '../types'

export const orderApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getOrderList: build.query<ApiResponse<OrderListResponse>, string>({
      query: (filter) => ({
        url: `order?${filter}`,
        method: 'GET'
      }),
      providesTags: ['ORDER_LIST']
    }),
    searchOrders: build.query<ApiResponse<OrderListResponse>, string>({
      query: (filter) => ({
        url: `order/search?${filter}`,
        method: 'GET'
      }),
      providesTags: ['ORDER_LIST']
    }),
    getOrderById: build.query<TOrder, string>({
      query: (id) => ({
        url: `order/${id}`,
        method: 'GET'
      }),
      transformResponse: (response: ApiResponse<OrderItemResponse>) => response.data.order,
      forceRefetch: () => true
    }),
    getOrderCopy: build.query<OrderCopy, string>({
      query: (id) => ({
        url: `order/${id}/copy`,
        method: 'GET'
      }),
      transformResponse: (response: ApiResponse<OrderCopyToCreateNewOrderResponse>) => response.data.order,
      forceRefetch: () => true
    }),
    findCheckOutSchedule: build.query<TCheckOutSchedule[], TCheckOutScheduleRequestData>({
      query: ({ departureTerminalId, destinationTerminalId }) => ({
        url: `terminal/${departureTerminalId}/check-out-schedule/${destinationTerminalId}`,
        method: 'GET'
      }),
      transformResponse: (response: ApiResponse<TerminalCheckoutScheduleResponse>) => response.data.checkOutSchedule
    }),
    findTracking: build.query<NonNullable<TrackingResponse['data']>, string>({
      query: (barcode) => ({
        url: `order-tracking/${barcode}`,
        method: 'GET'
      }),
      transformResponse: (response: ApiResponse<TrackingResponse>) => response.data,
      forceRefetch: () => true
    }),
    cancelOrder: build.mutation<ApiResponse<OrderItemResponse>, { id: string; reason: string }>({
      query: ({ id, reason }) => ({
        url: `order/${id}/cancel`,
        method: 'PUT',
        body: {
          reason
        }
      })
    }),
    findOrderPayment: build.query<TOrderPayment[], string>({
      query: (id) => ({
        url: `order/${id}/payment`,
        method: 'GET'
      }),
      transformResponse: (response: ApiResponse<PaymentListResponse>) => response.data.payment
    }),
    createPaymentOrder: build.mutation<
      ApiResponse<PaymentTransactionCreateResponse>,
      PaymentTransactionCreateRequestWithId
    >({
      query: ({ email, id, method }) => ({
        url: `order/${id}/payment-transaction`,
        method: 'POST',
        body: {
          email,
          method
        }
      }),
      extraOptions: { isSilentOnSuccess: true }
    }),
    createOrder: build.mutation<ApiResponse<OrderItemResponse>, TOrderCreatePayload>({
      query: (body) => ({
        url: 'order',
        method: 'POST',
        body
      }),
      invalidatesTags: invalidateTagsIfSuccess(['ORDER_LIST'])
    }),
    createOrderYarche: build.mutation<ApiResponse<OrderItemResponse>, OrderYarcheCreateRequest>({
      query: (body) => ({
        url: 'order-yarche',
        method: 'POST',
        body
      }),
      invalidatesTags: invalidateTagsIfSuccess(['ORDER_LIST'])
    }),
    getAllYarcheTerminals: build.query<NonNullable<TerminalListResponse['data']>['terminal'], string>({
      query: () => ({
        url: 'terminal/yarche',
        method: 'GET'
      }),
      transformResponse: (response: ApiResponse<TerminalListResponse>) => (response ? response.data.terminal : []),
      extraOptions: { showErrors: false },
      forceRefetch: () => true
    }),
    getCommonOperations: build.query<NonNullable<OperationListResponse['data']>['operation'], void>({
      query: () => ({
        url: 'operation/common',
        method: 'GET'
      }),
      transformResponse: (response: ApiResponse<OperationListResponse>) => (response ? response.data.operation : []),
      forceRefetch: () => true
    }),
    getPointOperations: build.query<
      NonNullable<OperationListResponse['data']>['operation'],
      { type: 'departure' | 'destination' }
    >({
      query: ({ type }) => ({
        url: `operation/point?type=${type}`,
        method: 'GET'
      }),
      transformResponse: (response: ApiResponse<OperationListResponse>) => (response ? response.data.operation : []),
      forceRefetch: () => true
    }),
    getParamOperations: build.query<NonNullable<OperationParamListResponse['data']>['param'], void>({
      query: () => ({
        url: 'operation/param',
        method: 'GET'
      }),
      transformResponse: (response: ApiResponse<OperationParamListResponse>) => (response ? response.data.param : []),
      forceRefetch: () => true
    }),
    getParamTerminalOperations: build.query<
      NonNullable<OperationParamListResponse['data']>['param'],
      { id: string; type: 'departure' | 'destination' }
    >({
      query: ({ id, type }) => ({
        url: `operation/param/${id}?type=${type}`,
        method: 'GET'
      }),
      transformResponse: (response: ApiResponse<OperationParamListResponse>) => (response ? response.data.param : []),
      forceRefetch: () => true
    })
  }),
  overrideExisting: false
})

export const {
  useGetAllYarcheTerminalsQuery,
  useGetOrderListQuery,
  useSearchOrdersQuery,
  useGetOrderByIdQuery,
  useGetOrderCopyQuery,
  useFindCheckOutScheduleQuery,
  useLazyFindCheckOutScheduleQuery,
  useLazySearchOrdersQuery,
  useFindTrackingQuery,
  useCancelOrderMutation,
  useFindOrderPaymentQuery,
  useCreateOrderMutation,
  useCreateOrderYarcheMutation,
  useCreatePaymentOrderMutation,
  useGetCommonOperationsQuery,
  useLazyGetCommonOperationsQuery,
  useGetPointOperationsQuery,
  useLazyGetPointOperationsQuery,
  useLazyGetParamTerminalOperationsQuery,
  useGetParamOperationsQuery
} = orderApi
